import useMutationApi from '../../useMutationApi';
import { SupportedGPTModels } from 'src/pages/authenticated/core/spreadsheets/types/spreadsheetTypes';
import type { WorkflowConstants } from 'src/pages/authenticated/core/spreadsheets/types/WorkflowConstants';

interface WorkflowMapFieldsRequest {
  data: string; // cleanedCsv from preprocess phase
  instructions: string;
  fileNames: string[];
  sheetName: string;
  model: SupportedGPTModels;
  constants?: WorkflowConstants;
  testMode?: boolean;
}

// How a mapping was determined
export type MappingMatchSource = 'direct' | 'alias' | 'ai' | 'user';

// Discriminated union for simple vs split mappings
export type MappingValue = SimpleMappingValue | SplitMappingValue;

export interface SimpleMappingValue {
  type: 'simple';
  value: string;
  /** How this mapping was determined: 'direct' = exact name match, 'alias' = matched via alias, 'ai' = AI determined, 'user' = user override */
  matchSource?: MappingMatchSource;
}

export interface SplitMappingValue {
  type: 'split';
  splits: Array<{
    value: string; // Target value (e.g., "Apples")
    percentage: number; // 0-100 (e.g., 20.5 for 20.5%)
  }>;
  /** How this mapping was determined: 'direct' = exact name match, 'alias' = matched via alias, 'ai' = AI determined, 'user' = user override */
  matchSource?: MappingMatchSource;
}

interface CompositeMapping {
  sourceColumnNames: string[]; // e.g., ["Division", "City", "State"]
  targetColumnName: string; // e.g., "DivisionMapping"
  mappings: Record<string, string | MappingValue>; // Support both old (string) and new (MappingValue) formats
  separator?: string; // default "|||"
}

interface FieldMappings {
  // Required mappings - always present, use "*": "UNKNOWN" if AI can't determine
  divisionMappings: CompositeMapping;
  productMappings: CompositeMapping;
  // Optional mappings - only present when relevant data is found
  dateMappings?: CompositeMapping;
}

interface WorkflowMapFieldsResponse {
  mappings: FieldMappings;
  explanations: string[];
}

const useWorkflowMapFields = () => {
  return useMutationApi<WorkflowMapFieldsRequest, WorkflowMapFieldsResponse>({
    endpoint: 'spreadsheet/workflow/map-fields',
    method: 'POST',
    successMessage: 'Field mapping completed successfully',
    showSuccessToast: false, // Let the UI handle success feedback
  });
};

export default useWorkflowMapFields;
export type {
  WorkflowMapFieldsRequest,
  WorkflowMapFieldsResponse,
  CompositeMapping,
  FieldMappings,
};
